import React, { useState, useEffect } from 'react';
import { userService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre: '',
    email: '',
    role: 'ROLE_USER'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userService.getAllUsers();
      if (response.success) {
        setUsers(response.data || []);
      } else {
        setError(response.message || 'Error al cargar usuarios');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al cargar usuarios');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        const updateData = {
          nombre: formData.nombre,
          email: formData.email,
          role: formData.role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        const response = await userService.updateUser(editingUser.id, updateData);
        if (response.success) {
          setUsers(users.map(u => u.id === editingUser.id ? response.data : u));
          resetForm();
        } else {
          setError(response.message || 'Error al guardar usuario');
        }
      } else {
        const response = await userService.createUser(formData);
        if (response.success) {
          setUsers([...users, response.data]);
          resetForm();
        } else {
          setError(response.message || 'Error al guardar usuario');
        }
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al guardar usuario');
      setError(message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      nombre: user.nombre,
      email: user.email,
      role: user.role
    });
    setShowForm(true);
  };

  const handleToggleStatus = async (id) => {
    try {
      setError('');
      const response = await userService.toggleUserStatus(id);
      if (response.success) {
        setUsers(users.map(u => u.id === id ? response.data : u));
      } else {
        setError(response.message || 'Error al cambiar estado del usuario');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al cambiar estado del usuario');
      setError(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) {
      return;
    }

    try {
      setError('');
      const response = await userService.deleteUser(id);
      if (response.success) {
        setUsers(users.filter(u => u.id !== id));
      } else {
        setError(response.message || 'Error al eliminar usuario');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al eliminar usuario');
      setError(message);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      nombre: '',
      email: '',
      role: 'ROLE_USER'
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'ROLE_SUPER_ADMIN':
        return 'Super Administrador';
      case 'ROLE_USER':
        return 'Usuario';
      default:
        return role;
    }
  };

  if (loading) {
    return <p>Cargando usuarios...</p>;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h3>Gestión de Usuarios</h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            Nuevo Usuario
          </button>
        )}
      </div>

      <ErrorDisplay error={error} />

      {showForm && (
        <div className="user-form-container">
          <h4>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h4>
          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-group">
              <label htmlFor="username">Usuario:</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                disabled={!!editingUser}
                required={!editingUser}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                {editingUser ? 'Nueva Contraseña (dejar vacío para mantener):' : 'Contraseña:'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="nombre">Nombre:</label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Rol:</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="ROLE_USER">Usuario</option>
                <option value="ROLE_SUPER_ADMIN">Super Administrador</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Actualizar' : 'Crear'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.enabled ? 'disabled-user' : ''}>
                <td>{user.username}</td>
                <td>{user.nombre}</td>
                <td>{user.email}</td>
                <td>{getRoleLabel(user.role)}</td>
                <td>
                  <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                    {user.enabled ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="actions-cell">
                  <button
                    onClick={() => handleEdit(user)}
                    className="btn btn-small btn-secondary"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleToggleStatus(user.id)}
                    className={`btn btn-small ${user.enabled ? 'btn-warning' : 'btn-success'}`}
                  >
                    {user.enabled ? 'Desactivar' : 'Activar'}
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="btn btn-small btn-danger"
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserManagement;
